import React, { useState } from "react";
import { CollapsibleNavItemComponent } from "../../services/types/ComponentTypes";
import { NavItem } from "./NavItem";

interface CollapsibleNavItemProps {
  config: CollapsibleNavItemComponent;
  isActive?: boolean;
  onSubItemClick?: (route?: string, action?: string) => void;
  expandedItems?: Set<string>;
  onToggleExpanded?: (itemName: string) => void;
  activeView?: string;
  activeRoute?: string;
}

export const CollapsibleNavItem: React.FC<CollapsibleNavItemProps> = ({
  config,
  isActive = false,
  onSubItemClick,
  expandedItems,
  onToggleExpanded,
  activeView,
  activeRoute,
}) => {
  const [localExpanded, setLocalExpanded] = useState(
    config.defaultExpanded || false
  );
  const [showPopup, setShowPopup] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isExpanded = expandedItems
    ? expandedItems.has(config.name)
    : localExpanded;

  const submenuMode = config.submenuMode || "inline";

  const handleToggle = () => {
    if (config.disabled) return;

    if (submenuMode === "inline") {
      if (onToggleExpanded) {
        onToggleExpanded(config.name);
      } else {
        setLocalExpanded(!localExpanded);
      }
    } else {
      setShowPopup(!showPopup);
    }
  };

  const handleMouseEnter = () => {
    if (submenuMode === "outside") {
      setShowPopup(true);
    }
  };

  const handleMouseLeave = () => {
    if (submenuMode === "outside") {
      setShowPopup(false);
    }
  };

  const baseStyles: React.CSSProperties = {
    padding: config.style?.padding || 6,
    margin: config.style?.margin || 2,
    backgroundColor: isActive
      ? "rgba(255, 255, 255, 0.1)"
      : config.style?.backgroundColor || "transparent",
    color: config.style?.textColor || "#FFFFFF",
    border: config.style?.borderWidth
      ? `${config.style.borderWidth}px solid ${
          config.style.borderColor || "transparent"
        }`
      : "none",
    borderRadius: config.style?.borderRadius || 8,
    fontSize: config.style?.fontSize || 12,
    fontWeight: config.style?.fontWeight || "normal",
  };

  const hoverBgClass = isActive ? "bg-white/15" : "bg-white/8";
  const iconSize = config.style?.fontSize ? config.style.fontSize * 1.2 : 16;
  const labelFontWeight = isActive ? "600" : config.style?.fontWeight || "500";

  // Check if icon is an image path (starts with / or http, or contains image extensions)
  const isImageIcon =
    config.icon &&
    typeof config.icon === "string" &&
    (config.icon.startsWith("/") ||
      config.icon.startsWith("http") ||
      /\.(webp|png|jpg|jpeg|svg|gif)$/i.test(config.icon));

  const popupMenuStyles: React.CSSProperties = {
    backgroundColor: config.submenuBackgroundColor || "#388E3C",
    color: config.submenuTextColor || "#FFFFFF",
    borderRadius: config.style?.borderRadius || 8,
  };

  return (
    <div
      className="relative"
      onMouseEnter={submenuMode === "outside" ? handleMouseEnter : undefined}
      onMouseLeave={submenuMode === "outside" ? handleMouseLeave : undefined}
    >
      {/* Parent Menu Item */}
      <div className="pb-1">
        <a
          href="#"
          className={`flex items-center px-4 py-2 no-underline text-xs text-gray-300 dark:text-gray-300 hover:bg-gray-700/50 dark:hover:bg-gray-700/50 hover:text-white dark:hover:text-white transition-all duration-200 rounded-md mx-1 ${
            config.disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
          } ${isActive || isExpanded ? "text-white dark:text-white border-l-4 border-primary-500 dark:border-primary-500 bg-gray-700/80 dark:bg-gray-700/80 shadow-sm" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            handleToggle();
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          title={config.tooltip || config.label}
        >
          {config.icon && (
            <span className="w-4 h-4 mr-2.5 flex items-center justify-center flex-shrink-0">
              {isImageIcon ? (
                <img
                  src={config.icon}
                  alt={config.label || "icon"}
                  className="w-4 h-4"
                  style={{
                    objectFit: "contain",
                  }}
                />
              ) : typeof config.icon === "string" && config.icon.trim().startsWith("<svg") ? (
                <span 
                  className="w-4 h-4 flex items-center justify-center"
                  dangerouslySetInnerHTML={{ __html: config.icon }}
                />
              ) : (
                <span className="text-sm">{config.icon}</span>
              )}
            </span>
          )}
          <span className="flex-1 text-xs">
            {config.label}
          </span>
          {submenuMode === "inline" && (
            <svg className={`w-3 h-3 ml-auto text-gray-400 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </a>

      {/* Submenu Items - Inline Mode */}
      {submenuMode === "inline" && (
        <div className={`space-y-0.5 pl-10 pr-3 mt-0.5 transition-all duration-300 ${
          isExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"
        }`}>
          {config.submenuItems.map((subItem, index) => {
            // Determine if this submenu item is active
            const isSubItemActive = !!(
              (activeView && subItem.onPressAction === activeView) ||
              (activeRoute && subItem.route === activeRoute) ||
              (activeView && subItem.name === activeView)
            );

            return (
              <a
                key={`${subItem.name}-${index}`}
                href="#"
                className={`block py-1.5 no-underline text-xs text-gray-400 hover:text-white hover:bg-gray-700/30 transition-all duration-200 rounded-md ${
                  isSubItemActive ? "text-white bg-gray-700/60 rounded-md px-2 shadow-sm" : "px-2"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  if (onSubItemClick) {
                    onSubItemClick(subItem.route, subItem.onPressAction);
                  }
                }}
              >
                {subItem.label}
              </a>
            );
          })}
        </div>
      )}
      </div>

      {/* Submenu Items - Outside/Popup Mode */}
      {submenuMode === "outside" && (
        <div
          className={`absolute left-full top-0 min-w-[200px] rounded-md shadow-lg p-2 ml-2 z-[1000] transition-opacity duration-200 ${
            showPopup ? "block opacity-100" : "hidden opacity-0"
          }`}
          style={popupMenuStyles}
        >
          {config.submenuItems.map((subItem, index) => {
            // Determine if this submenu item is active
            const isSubItemActive = !!(
              (activeView && subItem.onPressAction === activeView) ||
              (activeRoute && subItem.route === activeRoute) ||
              (activeView && subItem.name === activeView)
            );

            return (
              <div key={`${subItem.name}-${index}`}>
                <NavItem
                  config={{
                    ...subItem,
                    style: {
                      ...subItem.style,
                      fontSize: (config.style?.fontSize || 12) * 0.9,
                      padding: typeof config.style?.padding === 'number' ? Math.round(config.style.padding * 0.75) : (config.style?.padding || 9),
                      backgroundColor: isSubItemActive
                        ? "rgba(255, 255, 255, 0.1)"
                        : "transparent",
                      textColor: config.submenuTextColor || "#FFFFFF",
                    },
                  }}
                  isActive={isSubItemActive}
                  onClick={onSubItemClick}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
